import React, { useMemo, useState } from "react";
import { v4 as uuid } from "uuid";

import { TicketAndPassesForm } from "Bands/components";
import { useBandContext } from "Bands/contexts";
import { IGuestlistTicketAndPassParams } from "Bands/types";
import { copy } from "shared/assets";
import { Card, Title } from "shared/components";
import { Spacer } from "shared/layout";
import { ICardTheme, IFormButtons } from "shared/types";
import { TicketAndPassTable, EditTicketAndPassModal } from "Bands/containers";

const ticketAndPassCopy = copy.guestlistBlock.ticketAndPasses.create;

interface CreateTicketAndPassProps {
  ticketAndPasses: IGuestlistTicketAndPassParams[];
  setTicketAndPasses: React.Dispatch<React.SetStateAction<IGuestlistTicketAndPassParams[]>>;
}

export const CreateTicketAndPass: React.FC<CreateTicketAndPassProps> = ({
  ticketAndPasses,
  setTicketAndPasses,
}) => {
  const { ticketAndPassTypes } = useBandContext();

  const [selectedTicketAndPass, setSelectedTicketAndPass] =
    useState<IGuestlistTicketAndPassParams | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const handleDelete = (id?: string) => {
    if (id) {
      setTicketAndPasses(ticketAndPasses =>
        ticketAndPasses.filter(ticketAndPass => ticketAndPass.id !== id)
      );
    }
  };

  const handleEdit = (newTicketAndPass: IGuestlistTicketAndPassParams, id?: string) => {
    if (id) {
      setTicketAndPasses(ticketAndPasses =>
        ticketAndPasses.map(oldTicketAndPass =>
          oldTicketAndPass.id === id ? { ...newTicketAndPass, id } : oldTicketAndPass
        )
      );
      setShowEditModal(false);
    }
  };

  const openEditModal = (ticketAndPass: IGuestlistTicketAndPassParams) => {
    setShowEditModal(true);
    setSelectedTicketAndPass(ticketAndPass);
  };

  const handleAdd = ({ ticketsTotal, isUnlimited, ...rest }: IGuestlistTicketAndPassParams) => {
    const cleanTicketAndPass: IGuestlistTicketAndPassParams = {
      ...rest,
      id: uuid(),
      ticketsTotal: isUnlimited ? null : ticketsTotal,
      isUnlimited: !!isUnlimited,
    };

    setTicketAndPasses(ticketAndPasses => [...ticketAndPasses, cleanTicketAndPass]);
  };

  const defaultValues: IGuestlistTicketAndPassParams = useMemo(
    () => ({
      ticketsTotal: 1,
    }),
    []
  );

  const buttons: IFormButtons = useMemo(
    () => ({
      primary: {
        children: ticketAndPassCopy.buttons.create,
        size: "m",
        "data-testid": "ticket_pass_submit",
      },
    }),
    []
  );

  const filteredTicketAndPassTypes = useMemo(
    () =>
      ticketAndPassTypes.filter(
        ({ id }) => !ticketAndPasses.find(({ ticketAndPassType }) => ticketAndPassType === id)
      ),
    [ticketAndPassTypes, ticketAndPasses]
  );

  const ticketAndPassesWithLabels = useMemo(
    () =>
      ticketAndPasses
        .sort((a, b) => {
          const aIndex = ticketAndPassTypes.findIndex(type => type.id === a.ticketAndPassType);
          const bIndex = ticketAndPassTypes.findIndex(type => type.id === b.ticketAndPassType);

          return aIndex - bIndex;
        })
        .map(item => ({
          ...item,
          label: ticketAndPassTypes.find(({ id }) => item.ticketAndPassType?.includes(id))?.label,
        }))
        .filter(({ label }) => !!label),
    [ticketAndPassTypes, ticketAndPasses]
  );

  return (
    <Card theme={ICardTheme.secondary}>
      {showEditModal && !!selectedTicketAndPass && (
        <EditTicketAndPassModal
          ticketAndPass={selectedTicketAndPass}
          handleEdit={handleEdit}
          handleClose={() => setShowEditModal(false)}
        />
      )}
      <Spacer size="m">
        <Title size="m" asComponent="h3">
          {ticketAndPassCopy.title}
        </Title>

        <TicketAndPassesForm
          resetForm
          defaultValues={defaultValues}
          ticketAndPassTypes={filteredTicketAndPassTypes}
          buttons={buttons}
          handleSubmit={handleAdd}
        />

        <TicketAndPassTable
          ticketAndPasses={ticketAndPassesWithLabels}
          handleEdit={openEditModal}
          handleDelete={handleDelete}
        />
      </Spacer>
    </Card>
  );
};
